import { Type } from 'class-transformer';
import {
  IsBoolean,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../base.dto';
import { ConditionImageDto } from '../condition-info/condition-image.dtos';
import { MedicalUnitDto } from '../medical-unit.dtos';
import { ModelViewerLocation } from '../model-viewer-location.dto';
import { HealthRecordDto } from '../patient/health-record.dtos';
import { ConditionTriageDto } from './condition-triage.dtos';
import { ConditionTriageFormSubmissionDto } from './condition-triage-form-submission.dtos';
import type { MedicalRecordDto } from '../medical-record.dtos';

export class CreateConditionDto {
  @IsOptional()
  @IsString()
  preciseLocation?: string;

  @IsNotEmpty()
  @IsUUID()
  healthRecordId: string;

  @IsOptional()
  @IsUUID()
  conditionTypeId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionTypeId */
  skinConditionTypeId?: string;

  // FHIR reference (e.g. "Encounter/123") of the encounter that initiated this
  // condition. Forwarded to the auto-created first triage so it is linked
  // to the encounter context for downstream FHIR order resolution.
  @IsOptional()
  @IsString()
  encounterFhirReference?: string;

  /**
   * Stored as the auto-created first triage's draft submission. A dynamic-form
   * case sends one so it carries its form from the moment it exists: a case is
   * routed as a form case by its submission, and a legacy draft has none.
   */
  @IsOptional()
  @ValidateNested()
  @Type(() => ConditionTriageFormSubmissionDto)
  formSubmission?: ConditionTriageFormSubmissionDto;
}

export class UpdateConditionDto {
  @IsOptional()
  @ValidateNested()
  preciseLocation?: ModelViewerLocation;
}

export class ConditionDto extends BaseDto {
  @IsUUID()
  conditionTypeId: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionTypeId */
  skinConditionTypeId?: string;

  // latest images in DB
  latestOverviewImage?: ConditionImageDto;
  latestCloseUpImage?: ConditionImageDto;
  latestDermoscopicImage?: ConditionImageDto;

  latestConditionTriage?: ConditionTriageDto;

  /** @deprecated Use latestConditionTriage */
  latestSkinConditionTriage?: ConditionTriageDto;

  conditionTriages?: ConditionTriageDto[];

  /** @deprecated Use conditionTriages */
  skinConditionTriages?: ConditionTriageDto[];

  @IsOptional()
  @IsBoolean()
  complete?: boolean;

  /**
   * The condition's current location, resolved by `ConditionCurrentLocationService`.
   *
   * @deprecated Kept for released clients. A reader holding a form submission
   * uses `readCurrentLocation` from `@dermloop/shared/form-templates`.
   */
  @IsOptional()
  @ValidateNested()
  preciseLocation?: ModelViewerLocation;

  @IsNotEmpty()
  @IsNumber()
  creationOrder: number;

  @IsNotEmpty()
  @IsUUID()
  healthRecordId: string;

  healthRecord?: HealthRecordDto;

  @IsOptional()
  @IsNotEmpty()
  @IsUUID()
  medicalUnitId?: string;

  medicalUnit?: MedicalUnitDto;

  dermoscopicImages?: ConditionImageDto[];

  overviewImages?: ConditionImageDto[];

  closeUpImages?: ConditionImageDto[];
}

export class ConditionPdfRequestDto {
  @IsOptional()
  @IsUUID()
  conditionId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionId */
  skinConditionId?: string;

  @IsUUID()
  medicalUnitId: string;

  @ValidateNested()
  medicalRecord: MedicalRecordDto;
}
